import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {WebServiceDto} from '../../../../generated-model/models';
import {WebServiceService} from './web-service-service';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../../shared/commons/toast/toast-service';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

/** ผู้ใช้งาน Web Service API — list (legacy com_rbasic view `wss`). */
@Component({
  selector: 'app-web-service',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './web-service.html',
  styleUrl: './web-service.scss',
})
export class WebServiceComponent implements OnInit {
  private readonly service = inject(WebServiceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<WebServiceDto[]>([]);
  /** id of the row whose state is currently being toggled (disables its badge button). */
  protected readonly togglingId = signal<number | null>(null);

  private totalElements = 0;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllWebServices(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toastService.error(this.errorMessage(err, 'โหลดข้อมูลผู้ใช้งาน Web Service API ไม่สำเร็จ'));
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  /** Click on the status badge toggles เผยแพร่ (1) <-> ไม่เผยแพร่ (0), like the legacy jgrid.published toggle. */
  async toggleState(item: WebServiceDto): Promise<void> {
    if (item.id == null || this.togglingId() != null) {
      return;
    }
    const nextState = item.state === 1 ? 0 : 1;
    const nextLabel = nextState === 1 ? 'เผยแพร่' : 'ไม่เผยแพร่';
    const confirmed = await this.confirmDialogService.confirm({
      title: 'ยืนยันการเปลี่ยนสถานะ',
      message: `ต้องการเปลี่ยนสถานะของ "${item.name || '-'}" เป็น "${nextLabel}" ใช่หรือไม่?`,
      confirmText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      confirmButtonClass: nextState === 1 ? 'btn-primary' : 'btn-danger',
    });
    if (!confirmed) {
      return;
    }
    this.togglingId.set(item.id);
    this.service.updateState(item.id, nextState).subscribe({
      next: () => {
        this.togglingId.set(null);
        this.toastService.success(`เปลี่ยนสถานะเป็น "${nextLabel}" เรียบร้อยแล้ว`);
        this.load();
      },
      error: (err) => {
        this.togglingId.set(null);
        this.toastService.error(this.errorMessage(err, 'เปลี่ยนสถานะไม่สำเร็จ'));
        this.cdr.detectChanges();
      }
    });
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteWebService(id).subscribe({
      next: () => {
        this.toastService.success('ลบข้อมูลเรียบร้อยแล้ว');
        this.load();
      },
      error: (err) => {
        this.toastService.error(this.errorMessage(err, 'ลบข้อมูลไม่สำเร็จ'));
        this.cdr.detectChanges();
      }
    });
  }

  private errorMessage(err: any, fallback: string): string {
    const msg = err?.error?.message ?? err?.error?.error ?? err?.message;
    return typeof msg === 'string' && msg.trim() ? msg : fallback;
  }
}
